/**
 * Talent/gear modification lines: diff a compiled (patched) AbilityDef against the base content
 * one and describe each change in plain words ("Shield Slam also interrupts ...").
 */
import type { AbilityDef, AuraDef, CastKind, ClassDef, EffectSpec, ResourceAmount } from '@keystone/sim';
import { auraClauses, condText, effectPhrase, procSentences, sameJson, type RenderCtx } from './effects';
import { capitalize, fmtDuration, fmtPct, fmtSec, trimNum } from './format';

export function castLabel(c: CastKind | undefined, haste = 0): string {
  const k = c ?? { type: 'instant' };
  if (k.type === 'instant') return 'Instant';
  if (k.type === 'cast') return `${fmtSec(k.timeMs / (1 + haste))} sec cast`;
  return `Channeled (${fmtSec(k.durationMs / (1 + haste))} sec)`;
}

function resourceDiff(field: 'cost' | 'gain', base: ResourceAmount[] | undefined, cur: ResourceAmount[] | undefined, ctx: RenderCtx): string[] {
  const out: string[] = [];
  const ids = new Set([...(base ?? []), ...(cur ?? [])].map((r) => r.resource));
  for (const id of ids) {
    const b = base?.find((r) => r.resource === id)?.amount ?? 0;
    const c = cur?.find((r) => r.resource === id)?.amount ?? 0;
    if (b === c) continue;
    const res = ctx.resourceName(id);
    if (field === 'gain') out.push(c === 0 ? `no longer generates ${res}` : `generates ${c} ${res}${b ? ` (was ${b})` : ''}`);
    else out.push(c === 0 ? `costs no ${res}` : `costs ${c} ${res}${b ? ` (was ${b})` : ''}`);
  }
  return out;
}

/** Radii of every area/shape in an effect tree, in walk order. */
function radii(effects: readonly EffectSpec[] | undefined): number[] {
  const out: number[] = [];
  const shapeR = (s: { type: string; radius?: number; range?: number; length?: number; outer?: number }): number | undefined => s.radius ?? s.range ?? s.length ?? s.outer;
  const walk = (list: readonly EffectSpec[] | undefined): void => {
    for (const e of list ?? []) {
      if (e.area) {
        const r = shapeR(e.area.shape as never);
        if (r !== undefined) out.push(r);
      }
      if (e.type === 'zone' || e.type === 'telegraph') {
        const r = shapeR(e.shape as never);
        if (r !== undefined) out.push(r);
      }
      if (e.type === 'conditional') {
        walk(e.then);
        walk(e.else);
      }
      if (e.type === 'telegraph') walk(e.effects);
    }
  };
  walk(effects);
  return out;
}

function maxTargets(effects: readonly EffectSpec[] | undefined): number[] {
  const out: number[] = [];
  const walk = (list: readonly EffectSpec[] | undefined): void => {
    for (const e of list ?? []) {
      if (e.area?.maxTargets) out.push(e.area.maxTargets);
      if (e.type === 'conditional') {
        walk(e.then);
        walk(e.else);
      }
    }
  };
  walk(effects);
  return out;
}

/** Sum of amount coefficients/bases of a kind (damage/heal/absorb) in a tree. */
function amountSum(effects: readonly EffectSpec[] | undefined, kind: 'damage' | 'heal' | 'absorb'): number {
  let s = 0;
  const walk = (list: readonly EffectSpec[] | undefined): void => {
    for (const e of list ?? []) {
      if (e.type === kind) s += (e.amount.coef ?? 0) * 1000 + (e.amount.base ?? 0);
      if (e.type === 'conditional') {
        walk(e.then);
        walk(e.else);
      }
      if (e.type === 'telegraph') walk(e.effects);
      if (e.type === 'zone') walk(e.tick?.effects);
    }
  };
  walk(effects);
  return s;
}

function inlineAuras(effects: readonly EffectSpec[] | undefined): AuraDef[] {
  const out: AuraDef[] = [];
  const walk = (list: readonly EffectSpec[] | undefined): void => {
    for (const e of list ?? []) {
      if (e.type === 'applyAura') out.push(e.aura);
      if (e.type === 'conditional') {
        walk(e.then);
        walk(e.else);
      }
      if (e.type === 'telegraph') walk(e.effects);
      if (e.type === 'zone') {
        walk(e.tick?.effects);
        if (e.insideAura) out.push(e.insideAura);
      }
    }
  };
  walk(effects);
  return out;
}

function effectListDiff(name: string, base: readonly EffectSpec[] | undefined, cur: readonly EffectSpec[] | undefined, ctx: RenderCtx): string[] {
  const b = base ?? [];
  const c = cur ?? [];
  if (sameJson(b, c)) return [];
  const out: string[] = [];
  const quiet: RenderCtx = { ...ctx, formulas: [] };
  // Added / removed whole effects (matched by deep equality).
  const unmatchedB = b.filter((x) => !c.some((y) => sameJson(x, y)));
  const unmatchedC = c.filter((y) => !b.some((x) => sameJson(x, y)));
  // Same-type changes pair up in order (a talent that adds an effect AND resizes another must not
  // pair the new effect with the old one); the rest are additions/removals.
  const restB: EffectSpec[] = [];
  for (const x of unmatchedB) {
    const j = unmatchedC.findIndex((y) => y.type === x.type);
    if (j < 0) {
      restB.push(x);
      continue;
    }
    const y = unmatchedC.splice(j, 1)[0]!;
    out.push(...modifiedEffect(name, x, y, quiet));
  }
  const pairs = Math.min(restB.length, unmatchedC.length);
  for (let i = 0; i < pairs; i++) out.push(`${name} now ${effectPhrase(unmatchedC[i]!, quiet)}`);
  unmatchedB.splice(0, unmatchedB.length, ...restB);
  for (const y of unmatchedC.slice(pairs)) {
    const p = effectPhrase(y, quiet);
    if (p) out.push(/^if /.test(p) ? `${name}: ${p}` : `${name} also ${p}`);
  }
  for (const x of unmatchedB.slice(pairs)) {
    const p = effectPhrase(x, quiet);
    if (p) out.push(`${name} no longer ${p}`);
  }
  return out;
}

function modifiedEffect(name: string, x: EffectSpec, y: EffectSpec, ctx: RenderCtx): string[] {
  const out: string[] = [];
  for (const kind of ['damage', 'heal', 'absorb'] as const) {
    const a = amountSum([x], kind);
    const b = amountSum([y], kind);
    if (a > 0 && b > 0 && Math.abs(b / a - 1) > 1e-6) out.push(`${name} ${kind === 'absorb' ? 'shield' : kind} ${b > a ? 'increased' : 'reduced'} by ${fmtPct(Math.abs(b / a - 1))}`);
  }
  const ra = radii([x]);
  const rb = radii([y]);
  if (ra.length && ra.length === rb.length && ra.some((r, i) => r !== rb[i])) {
    const k = rb[0]! / ra[0]!;
    out.push(`${name} radius ${k > 1 ? 'increased' : 'reduced'} by ${fmtPct(Math.abs(k - 1))} (${trimNum(rb[0]!, 1)} yd)`);
  }
  const ta = maxTargets([x]);
  const tb = maxTargets([y]);
  if (ta.length && ta.length === tb.length && ta.some((t, i) => t !== tb[i])) {
    const d = tb[0]! - ta[0]!;
    out.push(`${name} hits ${d > 0 ? `${d} more` : `${-d} fewer`} target${Math.abs(d) === 1 ? '' : 's'} (up to ${tb[0]})`);
  }
  const aa = inlineAuras([x]);
  const ab = inlineAuras([y]);
  for (const nb of ab) {
    const old = aa.find((a) => a.id === nb.id);
    if (!old || sameJson(old, nb)) continue;
    if (old.durationMs !== nb.durationMs && nb.durationMs > 0) out.push(`${nb.name} lasts ${fmtDuration(nb.durationMs)} (was ${fmtDuration(old.durationMs)})`);
    const oc = auraClauses({ ...old, durationMs: nb.durationMs }, ctx);
    const nc = auraClauses(nb, ctx);
    const added = nc.filter((c) => !oc.includes(c));
    if (added.length) out.push(`${nb.name}: ${added.join('; ')}`);
  }
  if (!out.length) {
    const p = effectPhrase(y, ctx);
    out.push(p ? `${name} now ${p}` : `${name} effect modified`);
  }
  return out;
}

/** Plain-language modification lines (no "Talent:" prefix, lowercase-first allowed). */
export function diffAbility(base: AbilityDef, cur: AbilityDef, ctx: RenderCtx): string[] {
  if (sameJson(base, cur)) return [];
  const out: string[] = [];
  const name = cur.name;
  out.push(...resourceDiff('cost', base.cost, cur.cost, ctx), ...resourceDiff('gain', base.gain, cur.gain, ctx));
  const bc = base.cooldown;
  const cc = cur.cooldown;
  if (!sameJson(bc, cc)) {
    if (bc && !cc) out.push('no cooldown');
    else if (cc) {
      if ((cc.charges ?? 1) !== (bc?.charges ?? 1)) out.push(`${cc.charges ?? 1} charges`);
      if (cc.durationMs !== bc?.durationMs) out.push(`${fmtDuration(cc.durationMs)} cooldown${bc ? ` (was ${fmtDuration(bc.durationMs)})` : ''}`);
    }
  }
  if (!sameJson(base.cast, cur.cast)) out.push(`${castLabel(cur.cast)} (was ${castLabel(base.cast)})`);
  if (base.range !== cur.range && cur.range) out.push(`${trimNum(cur.range, 1)} yd range${base.range ? ` (was ${trimNum(base.range, 1)} yd)` : ''}`);
  if (base.targeting !== cur.targeting) out.push(`targeting changed to ${cur.targeting}`);
  if ((base.gcdMs ?? 1500) !== (cur.gcdMs ?? 1500)) out.push(cur.gcdMs === 0 ? 'off the global cooldown' : `${fmtSec(cur.gcdMs ?? 1500)} sec global cooldown`);
  if (!!base.canCastWhileMoving !== !!cur.canCastWhileMoving) out.push(cur.canCastWhileMoving ? 'usable while moving' : 'no longer usable while moving');
  if (!!base.usableWhileCasting !== !!cur.usableWhileCasting && cur.usableWhileCasting) out.push('usable while casting');
  if (!!base.requiresFacing !== !!cur.requiresFacing) out.push(cur.requiresFacing ? 'requires facing the target' : 'no longer requires facing');
  if (base.school !== cur.school) out.push(`deals ${cur.school} damage`);
  if (!sameJson(base.usableIf, cur.usableIf)) out.push(cur.usableIf ? `usable only if ${condText(cur.usableIf, ctx) || 'its requirement is met'}` : 'no longer has a usage condition');
  out.push(...effectListDiff(name, base.effects, cur.effects, ctx));
  out.push(...effectListDiff(name, base.channelStartEffects, cur.channelStartEffects, ctx));
  out.push(...effectListDiff(`the end of ${name}`, base.channelEndEffects, cur.channelEndEffects, ctx));
  return out;
}

/**
 * Passives the compiled class gained (talents/gear) that mention this ability: mods filtered to it,
 * or procs keyed on it (phrased from the ability's point of view). Progression's stat auras are skipped.
 */
export function passiveLines(ability: AbilityDef, base: ClassDef | undefined, cur: Pick<ClassDef, 'passives'> | undefined, ctx: RenderCtx): string[] {
  if (!cur?.passives?.length) return [];
  const had = new Set((base?.passives ?? []).map((p) => p.id));
  const out: string[] = [];
  const quiet = { ...ctx, formulas: [] };
  for (const p of cur.passives) {
    if (had.has(p.id) || p.id.startsWith('progression.')) continue;
    const mods = (p.mods ?? []).filter((m) => m.abilityIds?.includes(ability.id));
    const procs = (p.procs ?? []).filter((pr) => pr.abilityIds?.includes(ability.id));
    if (!mods.length && !procs.length) continue;
    const clauses = [
      ...(mods.length ? auraClauses({ id: p.id, name: p.name, kind: 'buff', durationMs: 0, stacking: 'unique', mods }, quiet, { inline: true }) : []),
      ...procSentences(procs, quiet, { perspective: ability }),
    ];
    if (clauses.length) out.push(`${p.name}: ${clauses.map(capitalize).join('. ')}`);
  }
  return out;
}
